import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PipeStatusBadge } from "@/components/projects/pipe-status-badge"
import { PipeStatusPopoverContent } from "@/components/projects/pipe-status-popup"
import { ProjectFormDialog } from "@/components/projects/project-form-dialog"
import { OwnerCard } from "@/components/explore/details/owner-card"
import { TagPopoverContent } from "@/components/projects/tag-popup"
import { Pencil, Star, ArrowRightLeft, MapPin, Ruler, LandPlot, Grid3x3, CalendarDays, Compass, Building2, Copy, Check, Plus, ExternalLink } from "lucide-react"
import type { IParcel } from "@/types/explore"
import { PLU_COLORS } from "@/components/explore/panels/plu-colors"
import { PROPERTY_TYPE_CONFIG, ORIGIN_LABELS, STATUS_CONFIG, getScoreColor } from "@/lib/project-configs"
import type { Project, ProjectTag, PipeStatusRecord, PotentielPluZone, SharedProps } from "@/types"

type Props = {
  project: Project
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  pipeStatuses: PipeStatusRecord[]
  partnerOrganisations?: { id: string; name: string }[]
  constructionYear?: string | null
  corporationOwnerParcels?: IParcel[]
  pluZone?: PotentielPluZone | null
  firstParcelIdu?: string | null
  pluDoc?: { gpu_doc_id: string; nomfic: string } | null
  children?: React.ReactNode
}

function ScoreIndicator({ score, status }: { score: number; status: Project["status"] }) {
  const color = getScoreColor(score)
  const Icon = STATUS_CONFIG[status]?.icon ?? Star
  return (
    <span title={`Score ${score}/10`}>
      <Icon className="size-5 shrink-0" style={{ color }} />
    </span>
  )
}


export function ProjectSummary({ project, users, teams, project_tags, pipeStatuses, partnerOrganisations = [], constructionYear, corporationOwnerParcels = [], pluZone, firstParcelIdu, pluDoc, children }: Props) {
  const PropertyIcon = PROPERTY_TYPE_CONFIG[project.property_type]?.icon
  const { current_user } = usePage<SharedProps>().props
  const isAdmin = current_user?.role === "admin" || current_user?.is_super_admin
  const canTransfer = isAdmin && partnerOrganisations.length > 0

  const [pipeStatusOpen, setPipeStatusOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferTarget, setTransferTarget] = useState<string>("")
  const [transferring, setTransferring] = useState(false)
  const [copied, setCopied] = useState(false)
  const [tagOpen, setTagOpen] = useState(false)

  const handleTransfer = () => {
    if (!transferTarget) return
    setTransferring(true)
    router.patch(`/projects/${project.id}/transfer_organisation`, { organisation_id: transferTarget }, {
      onFinish: () => {
        setTransferring(false)
        setTransferOpen(false)
        setTransferTarget("")
      },
    })
  }

  const address = [project.address, [project.zip_code, project.city_name].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")

  const parcelsLabel = project.parcels?.length
    ? project.parcels.map((p) => `${p.prefix ? `${p.prefix} ` : ""}${p.section} ${p.number}`).join(", ")
    : null

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 p-5">
        {/* ── Primary: Name + Status + Score ── */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <ScoreIndicator score={project.lead_score ?? 0} status={project.status} />
                <h2 className="text-lg font-semibold leading-tight truncate">{project.name}</h2>
                <Popover open={pipeStatusOpen} onOpenChange={setPipeStatusOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" className="cursor-pointer shrink-0">
                      <PipeStatusBadge pipeStatus={project.pipe_status} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2">
                    <PipeStatusPopoverContent project={project} pipeStatuses={pipeStatuses} onSelect={() => setPipeStatusOpen(false)} />
                  </PopoverContent>
                </Popover>
              </div>
              {address && (
                <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground group/address">
                  <MapPin className="size-3 shrink-0" />
                  {address}
                  <button
                    type="button"
                    className="opacity-0 group-hover/address:opacity-100 transition-opacity cursor-pointer"
                    title="Copier l'adresse"
                    onClick={() => {
                      navigator.clipboard.writeText(address)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 1500)
                    }}
                  >
                    {copied ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3" />}
                  </button>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canTransfer && (
                <Button size="sm" variant="ghost" onClick={() => setTransferOpen(true)}>
                  <ArrowRightLeft className="mr-1.5 size-3.5" />
                  Transférer
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-1.5 size-3.5" />
                Modifier
              </Button>
            </div>
          </div>

          {/* Badges + Score */}
          <div className="flex flex-wrap items-center gap-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="shrink-0 cursor-default">
                    {PropertyIcon && <PropertyIcon className="size-3" />}
                    {PROPERTY_TYPE_CONFIG[project.property_type]?.label || project.property_type}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Type de bien</TooltipContent>
              </Tooltip>
              {project.house_surface && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="tabular-nums cursor-default">
                      <Ruler className="size-3" />
                      {Number(project.house_surface).toLocaleString("fr-FR")} m²
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Surface bâtie</TooltipContent>
                </Tooltip>
              )}
              {project.field_surface && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="tabular-nums cursor-default">
                      <LandPlot className="size-3" />
                      {Number(project.field_surface).toLocaleString("fr-FR")} m²
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Surface terrain</TooltipContent>
                </Tooltip>
              )}
              {parcelsLabel && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="cursor-default">
                      <Grid3x3 className="size-3" />
                      {parcelsLabel}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Parcelles</TooltipContent>
                </Tooltip>
              )}
              {constructionYear && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="cursor-default">
                      <CalendarDays className="size-3" />
                      {constructionYear}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Année de construction</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="cursor-default">
                    <Compass className="size-3" />
                    {ORIGIN_LABELS[project.origin] || project.origin}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Origine</TooltipContent>
              </Tooltip>
              {project.organisation?.name && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="cursor-default">
                      <Building2 className="size-3" />
                      {project.organisation.name}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Organisation</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
            {(project.project_tags || []).map((tag) => (
              <span
                key={tag.id}
                className="rounded-full border px-1.5 py-0 text-[10px] font-medium leading-4"
                style={{ borderColor: tag.color, color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
            <Popover open={tagOpen} onOpenChange={setTagOpen}>
              <PopoverTrigger asChild>
                <button className="text-muted-foreground inline-flex size-5 cursor-pointer items-center justify-center rounded transition-colors hover:bg-accent hover:text-foreground">
                  <Plus className="size-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0">
                <TagPopoverContent project={project} availableTags={project_tags} onClose={() => setTagOpen(false)} />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* ── Tertiary: Corporation owners + PLU/Links ── */}
        {(corporationOwnerParcels.length > 0 || pluZone || project.latitude) && (
          <div className="flex gap-6">
            {corporationOwnerParcels.length > 0 && (
              <div className="space-y-2 w-1/2 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60">Propriétaires personne morale</p>
                <div className="space-y-3">
                  {corporationOwnerParcels.map((parcel) => (
                    <OwnerCard key={parcel.id} parcel={parcel} />
                  ))}
                </div>
              </div>
            )}
            {(pluZone || project.latitude) && (
              <div className="space-y-2 w-1/2 min-w-0">
                {pluZone && (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60">Zone PLU</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: PLU_COLORS[pluZone.zoneType ?? ""] || PLU_COLORS.default }}
                      />
                      <span className="font-medium">{pluZone.libelle}</span>
                    </div>
                  </>
                )}
                {project.latitude && project.longitude && (
                  <div className="flex flex-col gap-1.5 pt-1">
                    <a
                      href={`https://immobilier.pappers.fr/?lat=${project.latitude}&lon=${project.longitude}&z=18.00${firstParcelIdu ? `&parcelle=${firstParcelIdu}` : ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      Pappers
                      <ExternalLink className="size-3" />
                    </a>
                    <a
                      href={`https://explore.data.gouv.fr/fr/immobilier?onglet=carte&filtre=tous&lat=${project.latitude}&lng=${project.longitude}&zoom=17.97`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      DVF
                      <ExternalLink className="size-3" />
                    </a>
                    {pluDoc && (
                      <a
                        href={`https://www.geoportail-urbanisme.gouv.fr/api/document/${pluDoc.gpu_doc_id}/files/${pluDoc.nomfic}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        Consulter le PLU
                        <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs slot */}
      {children && <div className="flex-1 border-t px-5 pt-3 pb-5">{children}</div>}

      <ProjectFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
        users={users}
        teams={teams}
        project_tags={project_tags}
        pipeStatuses={pipeStatuses}
      />

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transférer le projet</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le projet sera transféré vers l'organisation sélectionnée. Les utilisateurs et équipes assignés seront retirés.
            </p>
            <Select value={transferTarget} onValueChange={setTransferTarget}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une organisation..." />
              </SelectTrigger>
              <SelectContent>
                {partnerOrganisations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleTransfer} disabled={!transferTarget || transferring}>
              Transférer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
